/**
 * The P&ID field under a line end, shared by the line form and the edit dialog so both
 * offer an end's sheet the same way.
 */
import React, { useEffect } from 'react';
import { useTagSuggestions } from '../hooks/useRedundancySets';
import { EndAssetTag } from '../pnid/entityRef';

/**
 * The P&ID one end is on. An equipment or instrument end picks among this drawing and the
 * drawings that carry its tag; any other end takes free text. The shown sheet is always an option.
 */
export const EndSheetField: React.FC<{
  label: string;
  docId: string;
  assetTag: EndAssetTag | null;
  ownPnidNo: string;
  value: string;
  onChange: (sheet: string) => void;
  disabled?: boolean;
}> = ({ label, docId, assetTag, ownPnidNo, value, onChange, disabled }) => {
  const lookup = useTagSuggestions(docId, assetTag?.kind ?? 'equipment', assetTag?.tag ?? '', Boolean(assetTag));
  useEffect(() => {
    // A failed lookup leaves this drawing and the shown sheet to pick from.
    if (lookup.error) console.warn('P&ID suggestions failed', lookup.error);
  }, [lookup.error]);

  const fieldStyle: React.CSSProperties = { width: '100%', boxSizing: 'border-box' };
  if (!assetTag) {
    return (
      <input aria-label={label} className="pnid-field" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder="P&ID number" style={fieldStyle} disabled={disabled} />
    );
  }
  // The service flags the suggestions whose tag is this exact tag once separators are normalized.
  const tagDrawings = (lookup.data ?? []).filter((suggestion) => suggestion.normalized_match)
    .flatMap((suggestion) => suggestion.drawings);
  const sheets = [...new Set([ownPnidNo, ...tagDrawings, value])].filter((sheet) => sheet || sheet === value);
  return (
    <select aria-label={label} className="pnid-field" value={value} onChange={(e) => onChange(e.target.value)} style={fieldStyle}
      disabled={disabled}>
      {sheets.map((sheet) => (
        <option key={sheet} value={sheet}>
          {sheet || 'No P&ID number'}
        </option>
      ))}
    </select>
  );
};
